import { ChangeEvent, useEffect, useState } from "react"
import AuthErrors from "../components/Auth/AuthErrors"
import SocialAuth from "../components/Auth/SocialAuth"
import { NavLink } from "../components/NavLink"
import { event } from "../utils/gtag"
import {
  useSignInWithFacebook,
  useSignInWithGoogle,
  useSignInWithTwitter,
} from "react-firebase-hooks/auth"
import createFirebaseApp from "../firebase/firebase"
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth"
import getRefinedFirebaseAuthErrorMessage from "../utils/firebase"
import useSessionStore from "../stores/session"
import useRouterStore from "stores/routerStore"
import useT from "utils/useT"
import isValidEmail from "utils/isValidEmail"
import useUpdateUser from "utils/useUpdateUser"
import ProfileImage from "components/ProfileImage"
import NameInput from "components/NameInput"
import EmailInput from "components/EmailInput"
import BioInput from "components/BioInput"
import Checkbox from "components/Checkbox"
import SocialMediaInputs from "components/SocialMediaInputs"
import isValidLink from "utils/isValidLink"

const app = createFirebaseApp()
const auth = getAuth(app)

interface SignupInterface {
  name: string
  email: string
  photoURL?: Blob
  password: string
  confirmPassword: string
  bio: string
}

const Signup = () => {
  const t = useT()
  const push = useRouterStore((state) => state.router?.push)
  const { updateUserMutation, isPending } = useUpdateUser()
  const [fileSizeError, setFileSizeError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [addSocialLinks, setAddSocialLinks] = useState(false)
  const [loading, setPending] = useState(false)
  const [signup, setSignup] = useState<SignupInterface>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    bio: "",
  })
  const [links, setLinks] = useState({
    facebookLink: "",
    twitterLink: "",
    instaLink: "",
    OtherLink: "",
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [signInWithFacebook, facebookUser, facebookLoading, facebookError] =
    useSignInWithFacebook(auth)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [signInWithGoogle, googleUser, googleLoading, googleError] =
    useSignInWithGoogle(auth)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [signInWithTwitter, twitterUser, twitterLoading, twitterError] =
    useSignInWithTwitter(auth)

  const isLoggedIn = useSessionStore((state) => state.isLoggedIn)

  useEffect(() => {
    isLoggedIn && push?.("/")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { name, email, photoURL, password, confirmPassword, bio } = signup
  const validationIcon = () => {
    if (password === "" || confirmPassword === "")
      return <em>({t.signup.required})</em>
    if (password === confirmPassword)
      return <i className="fa fa-check valid color-green-dark" />
    if (password !== confirmPassword)
      return <i className="fa fa-times invalid color-red-dark" />
  }
  const uploadFile = (file: Blob) => {
    if (file.size > 5000000) {
      setFileSizeError(true)
    } else {
      setFileSizeError(false)
    }

    setSignup((prevState) => ({ ...prevState, photoURL: file }))
  }

  const signUpWithEmailPassword = async () => {
    // [START auth_signup_password]z
    if (password !== confirmPassword && !name && !email) return
    setPending(true)
    !!errorMessage && setErrorMessage("")

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      )

      await updateUserMutation({
        user: userCredential.user,
        bio,
        email,
        name,
        photoURL,
        ...(addSocialLinks && { links: links }),
      })

      event({
        action: "sign_up",
        category: "Accounts",
        label: "Account created",
        value: `User Name: ${name}`,
      })
      setPending(false)
      // reset errors after successful signup
      !!errorMessage && setErrorMessage("")

      push?.("/")
    } catch (error: any) {
      console.log(error)
      const errorMessage = error?.message
      errorMessage && setErrorMessage(errorMessage)
      setPending(false)
    }

    // [END auth_signup_password]
  }

  const formattedError = getRefinedFirebaseAuthErrorMessage(errorMessage)
  const formattedGoogleError = getRefinedFirebaseAuthErrorMessage(
    googleError?.message,
  )

  const formattedFacebookError = getRefinedFirebaseAuthErrorMessage(
    facebookError?.message,
  )

  const formattedTwitterError = getRefinedFirebaseAuthErrorMessage(
    twitterError?.message,
  )

  const handleForm = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSignup((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }
  const invalidSocialLinks =
    addSocialLinks &&
    ((!!links.facebookLink && !isValidLink(links.facebookLink)) ||
      (!!links.instaLink && !isValidLink(links.instaLink)) ||
      (!!links.twitterLink && !isValidLink(links.twitterLink)) ||
      (!!links.OtherLink && !isValidLink(links.OtherLink)))

  return (
    <div className="card card-style">
      <div className="content mt-3">
        <div className="w-100 text-center mb-3 d-flex justify-content-center">
          <ProfileImage photoURL={photoURL} onFileUpload={uploadFile} />
        </div>
        {fileSizeError && (
          <p className="text-danger text-center">{t.signup.fileSizeError}</p>
        )}
        <NameInput value={name} onChange={handleForm} />
        <EmailInput value={email} onChange={handleForm} />
        <div className="input-style has-borders has-icon validate-field mt-2">
          <i className="fa fa-lock" />
          <input
            type="password"
            className="form-control validate-password"
            name="password"
            value={password}
            placeholder={t.signup.chooseAPassword}
            onChange={handleForm}
          />
          {validationIcon()}
        </div>
        <div className="input-style has-borders has-icon validate-field mt-2">
          <i className="fa fa-lock" />
          <input
            type="password"
            className="form-control validate-password"
            name="confirmPassword"
            value={confirmPassword}
            placeholder={t.signup.confirmYourPassword}
            onChange={handleForm}
          />

          {validationIcon()}
        </div>
        <BioInput value={bio} onChange={handleForm} />
        <Checkbox
          label={t.signup.socialCheckbox}
          checked={addSocialLinks}
          onChange={(e) => setAddSocialLinks(e.target.checked)}
        />
        {addSocialLinks && (
          <SocialMediaInputs links={links} setLinks={setLinks} />
        )}
        <button
          disabled={
            !Boolean(password === confirmPassword && name && email) ||
            loading ||
            isPending ||
            fileSizeError ||
            !isValidEmail(email) ||
            invalidSocialLinks
          }
          onClick={signUpWithEmailPassword}
          className="btn btn-m btn-full rounded-sm shadow-l bg-green-dark text-uppercase font-900 mt-4 w-100 mb-4"
        >
          {loading || isPending ? (
            <div className="spinner-border spinner-border-sm" role="status" />
          ) : (
            t.signup.createAccount
          )}
        </button>
        <AuthErrors
          errorMessage={formattedError}
          formattedFacebookError={formattedFacebookError}
          formattedGoogleError={formattedGoogleError}
          formattedTwitterError={formattedTwitterError}
        />
        <div className="divider"></div>
        <p className="text-center font-12 mt-n3">{t.signup.signInWith}</p>
        <SocialAuth
          signInWithFacebook={signInWithFacebook}
          facebookLoading={facebookLoading}
          signInWithGoogle={signInWithGoogle}
          googleLoading={googleLoading}
          signInWithTwitter={signInWithTwitter}
          twitterLoading={twitterLoading}
        />
        <div className="divider mt-4"></div>
        <div className="text-center">
          <NavLink href="/login" className="color-highlight opacity-80 font-12">
            {t.signup.alreadyRegistered}
          </NavLink>
        </div>
        <div id="firebaseui-auth-container" />
      </div>
    </div>
  )
}

export default Signup
